import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authApi from '../api/auth';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const data = await authApi.getMe();
      setUser(data.user);
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await authApi.getMe();
      setUser(data.user);
      return data.user;
    } catch (error) {
      // Don't clear auth on refresh failure
      throw error;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    // Clear any cached data from previous session
    queryClient.clear();
    const data = await authApi.login(email, password);
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  };

  const loginWithToken = async (token) => {
    // Clear any cached data from previous session
    queryClient.clear();
    // Store token first so the API call is authenticated
    localStorage.setItem('token', token);
    try {
      const data = await authApi.getMe();
      setUser(data.user);
      return data;
    } catch (error) {
      // If token is invalid, clear it
      localStorage.removeItem('token');
      throw error;
    }
  };

  const register = async (email, password, displayName) => {
    const data = await authApi.register(email, password, displayName);
    localStorage.setItem('token', data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('token');
    setUser(null);
    // Clear all cached data to prevent data leaking between accounts
    queryClient.clear();
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    loginWithToken,
    register,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
